// ADR-103 Rule DPD-03 — offline-first: only npm-installed, locally-bundled
// dependencies (zustand is already a project dependency per pishraft.md
// stack notes). No CDN scripts anywhere in this module.
import { create } from "zustand";
import type { CanvasElement, PageSize } from "../types";

interface DesignerState {
  elements: CanvasElement[];
  pageSize: PageSize;
  selectedElementId: string | null;

  setPageSize: (pageSize: PageSize) => void;
  addElement: (element: CanvasElement) => void;
  updateElement: (id: string, patch: Partial<CanvasElement>) => void;
  removeElement: (id: string) => void;
  assignElementToGroup: (elementId: string, targetGroupId: string | null) => void;
  selectElement: (id: string | null) => void;
  loadSchema: (elements: CanvasElement[], pageSize: PageSize) => void;
  reset: () => void;
}

export function nextElementId(): string {
  // Robust unique ID generation to prevent duplicate key collisions
  return `el_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
}

function updateElementRecursive(list: CanvasElement[], id: string, patch: Partial<CanvasElement>): CanvasElement[] {
  return list.map((el) => {
    if (el.id === id) {
      return { ...el, ...patch } as CanvasElement;
    }
    if (el.type === "group" && el.children) {
      return {
        ...el,
        children: updateElementRecursive(el.children, id, patch),
      };
    }
    return el;
  });
}

function removeElementRecursive(list: CanvasElement[], id: string): CanvasElement[] {
  return list
    .filter((el) => el.id !== id)
    .map((el) => {
      if (el.type === "group" && el.children) {
        return {
          ...el,
          children: removeElementRecursive(el.children, id),
        };
      }
      return el;
    });
}

export function findCanvasElement(items: CanvasElement[], id: string): CanvasElement | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.type === "group" && item.children) {
      const found = findCanvasElement(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

function findElementAndParent(
  list: CanvasElement[],
  id: string,
  parent: CanvasElement | null = null
): { found: CanvasElement | null; parent: CanvasElement | null } {
  for (const item of list) {
    if (item.id === id) {
      return { found: item, parent };
    }
    if (item.type === "group" && item.children) {
      const res = findElementAndParent(item.children, id, item);
      if (res.found) return res;
    }
  }
  return { found: null, parent: null };
}

function findElementAndRemove(list: CanvasElement[], id: string): { found: CanvasElement | null; updatedList: CanvasElement[] } {
  let found: CanvasElement | null = null;

  function traverse(items: CanvasElement[]): CanvasElement[] {
    const result: CanvasElement[] = [];
    for (const item of items) {
      if (item.id === id) {
        found = item;
      } else if (item.type === "group" && item.children) {
        result.push({
          ...item,
          children: traverse(item.children),
        });
      } else {
        result.push(item);
      }
    }
    return result;
  }

  const updatedList = traverse(list);
  return { found, updatedList };
}

function addElementToTargetGroup(list: CanvasElement[], targetGroupId: string, element: CanvasElement): CanvasElement[] {
  return list.map((el) => {
    if (el.id === targetGroupId && el.type === "group") {
      const children = el.children || [];
      const exists = children.some((c) => c.id === element.id);
      return {
        ...el,
        children: exists ? children.map((c) => (c.id === element.id ? element : c)) : [...children, element],
      };
    }
    if (el.type === "group" && el.children) {
      return {
        ...el,
        children: addElementToTargetGroup(el.children, targetGroupId, element),
      };
    }
    return el;
  });
}

export const useDesignerStore = create<DesignerState>((set) => ({
  elements: [],
  pageSize: "A4",
  selectedElementId: null,

  setPageSize: (pageSize) => set({ pageSize }),

  addElement: (element) =>
    set((state) => ({ elements: [...state.elements, element], selectedElementId: element.id })),

  updateElement: (id, patch) =>
    set((state) => ({
      elements: updateElementRecursive(state.elements, id, patch),
    })),

  removeElement: (id) =>
    set((state) => ({
      elements: removeElementRecursive(state.elements, id),
      selectedElementId: state.selectedElementId === id ? null : state.selectedElementId,
    })),

  assignElementToGroup: (elementId, targetGroupId) =>
    set((state) => {
      const { found, parent: currentParent } = findElementAndParent(state.elements, elementId);
      if (!found) return state;

      const currentParentId = currentParent?.id || null;
      if (currentParentId === (targetGroupId || null)) return state;

      const { updatedList } = findElementAndRemove(state.elements, elementId);

      const absoluteX = (currentParent ? currentParent.x : 0) + found.x;
      const absoluteY = (currentParent ? currentParent.y : 0) + found.y;

      let newX = found.x;
      let newY = found.y;

      if (targetGroupId) {
        const targetGroup = findCanvasElement(updatedList, targetGroupId);
        const targetX = targetGroup ? targetGroup.x : 0;
        const targetY = targetGroup ? targetGroup.y : 0;
        newX = Math.max(0, absoluteX - targetX);
        newY = Math.max(0, absoluteY - targetY);
      } else {
        newX = Math.max(0, absoluteX);
        newY = Math.max(0, absoluteY);
      }

      const updatedElement: CanvasElement = {
        ...found,
        group_id: targetGroupId || undefined,
        x: newX,
        y: newY,
      };

      if (!targetGroupId) {
        return {
          elements: [...updatedList, updatedElement],
        };
      }

      return {
        elements: addElementToTargetGroup(updatedList, targetGroupId, updatedElement),
      };
    }),

  selectElement: (id) => set({ selectedElementId: id }),

  loadSchema: (elements, pageSize) => set({ elements, pageSize, selectedElementId: null }),

  reset: () => set({ elements: [], pageSize: "A4", selectedElementId: null }),
}));
